/**
 * Policy record — Files tab (OR-1668, design OR-1639 "Files").
 *
 * Two levels, modelled on the Dropbox the users work with today:
 *  - Folders: one card per folder with its item count.
 *  - A folder: breadcrumb back to Files, a drag-and-drop upload area and
 *    the file table (File Name, Uploaded by, Upload date) with a ⋮ menu to
 *    preview, download or delete each file.
 *
 * States: the failed load ("Couldn't load files") with Try again, and the
 * shared "No Results Found" when the search matches nothing.
 *
 * Reads `GET /policy-terms/{id}/folders/` and each folder's files (backend
 * OR-1620). The backend is read-only for these files and serves downloads
 * as attachments, so Download works while Upload, Preview and Delete stay
 * disabled until it supports them.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MenuItem as AriaMenuItem, Separator as AriaSeparator } from "react-aria-components";
import { ChevronRight, Download01, Eye, Folder, RefreshCw05, Trash01, UploadCloud02 } from "@untitledui/icons";
import { Avatar, Button, FeaturedIcon, Table, cx } from "@tricura/atlas-ui";
import { type FolderSummary, type PolicyFile, listFolderFiles, listTermFolders } from "../../api";
import folderIcon from "../../assets/folder-icon.svg";
import { ListEmptyState } from "../../components/list-states";
import { ROW_ACTIONS_ITEM_CLASS, ROW_ACTIONS_SEPARATOR_CLASS, RowActionsMenu } from "../../components/row-actions-menu";
import { Skeleton } from "../../components/skeleton";
import { PlainHeader, SortHeader, parseSort } from "../../components/table-sort-header";
import { formatDate } from "./format";
import { TabState } from "./tab-state";

const initialsOf = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? "")
        .join("");

/** The presigned URL is served as an attachment, so following it downloads
 *  the file without leaving the page. */
function downloadFile(file: PolicyFile) {
    if (!file.downloadUrl) return;
    const a = document.createElement("a");
    a.href = file.downloadUrl;
    a.rel = "noopener";
    a.click();
}

interface FilesTabProps {
    termId: string;
    search: string;
}

export function FilesTab({ termId, search }: FilesTabProps) {
    const [folderId, setFolderId] = useState<string | null>(null);
    const [folders, setFolders] = useState<{ status: "loading" } | { status: "ready"; rows: FolderSummary[] } | { status: "error" }>({
        status: "loading",
    });
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setFolders({ status: "loading" });
        listTermFolders(termId)
            .then((rows) => !cancelled && setFolders({ status: "ready", rows }))
            .catch(() => !cancelled && setFolders({ status: "error" }));
        return () => {
            cancelled = true;
        };
    }, [termId, reloadKey]);

    // A different term starts back at the folder list.
    useEffect(() => setFolderId(null), [termId]);

    if (folders.status === "error") {
        return <FilesError onRetry={() => setReloadKey((k) => k + 1)} />;
    }

    const folder = folders.status === "ready" && folderId ? folders.rows.find((f) => f.id === folderId) : undefined;
    if (folder) {
        return (
            <FolderView
                termId={termId}
                folder={folder}
                search={search}
                onBack={() => setFolderId(null)}
            />
        );
    }

    const term = search.trim().toLowerCase();
    const visible = folders.status === "ready" ? folders.rows.filter((f) => !term || f.name.toLowerCase().includes(term)) : [];
    if (folders.status === "ready" && visible.length === 0) return <ListEmptyState description="Try a different search" />;

    return (
        <div className="atlas-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-6 md:px-5" data-autotest="policy-files-folders">
            <Heading title="Files" />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                {folders.status === "loading"
                    ? Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-[214px] rounded-xl" />)
                    : visible.map((f) => (
                          <button
                              key={f.id}
                              type="button"
                              onClick={() => setFolderId(f.id)}
                              data-autotest="policy-files-folder"
                              className="border-secondary bg-primary hover:border-primary outline-focus-ring flex cursor-pointer flex-col overflow-hidden rounded-xl border text-left shadow-xs transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2"
                          >
                              <span className="bg-secondary flex h-32 items-center justify-center">
                                  <img src={folderIcon} alt="" aria-hidden="true" className="size-14" />
                              </span>
                              <span className="flex flex-col gap-1 px-4 py-4">
                                  <span className="text-primary text-sm font-semibold">{f.name}</span>
                                  <span className="text-tertiary text-sm">{f.itemCount} items</span>
                              </span>
                          </button>
                      ))}
            </div>
        </div>
    );
}

/** Title over a breadcrumb trail, as both Files screens show it. */
function Heading({ title, folderName, onBack }: { title: string; folderName?: string; onBack?: () => void }) {
    return (
        <div className="flex flex-col gap-1 pt-4 pb-4">
            <h3 className="text-primary text-md font-semibold">{title}</h3>
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
                {folderName ? (
                    <>
                        <button type="button" onClick={onBack} className="text-tertiary hover:text-secondary cursor-pointer font-medium" data-autotest="policy-files-breadcrumb-root">
                            Files
                        </button>
                        <ChevronRight aria-hidden="true" className="text-fg-quaternary size-4" />
                        <span aria-current="page" className="text-brand-secondary font-semibold">
                            {folderName}
                        </span>
                    </>
                ) : (
                    <span aria-current="page" className="text-tertiary font-medium">
                        Files
                    </span>
                )}
            </nav>
        </div>
    );
}

function FilesError({ onRetry }: { onRetry: () => void }) {
    return (
        <TabState
            icon={Folder}
            title="Couldn't load files"
            description="We had some trouble loading the files."
            data-autotest="policy-files-state-error"
            action={
                <Button color="secondary" size="sm" iconTrailing={RefreshCw05} onClick={onRetry}>
                    Try again
                </Button>
            }
        />
    );
}

type FilesLoad = { status: "loading" } | { status: "ready"; rows: PolicyFile[] } | { status: "error" };

function FolderView({ termId, folder, search, onBack }: { termId: string; folder: FolderSummary; search: string; onBack: () => void }) {
    const [load, setLoad] = useState<FilesLoad>({ status: "loading" });
    const [reloadKey, setReloadKey] = useState(0);
    const [sort, setSort] = useState<string | undefined>(undefined);
    // react-aria's table doesn't spread percentage widths, so size the
    // columns from the measured wrapper (same approach as the other tables).
    const tableRef = useRef<HTMLTableElement>(null);
    const [tableWidth, setTableWidth] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoad({ status: "loading" });
        listFolderFiles(termId, folder.id)
            .then((rows) => !cancelled && setLoad({ status: "ready", rows }))
            .catch(() => !cancelled && setLoad({ status: "error" }));
        return () => {
            cancelled = true;
        };
    }, [termId, folder.id, reloadKey]);

    const rows = useMemo(() => {
        const list = load.status === "ready" ? load.rows : [];
        const term = search.trim().toLowerCase();
        const filtered = term ? list.filter((f) => f.name.toLowerCase().includes(term)) : list;
        const parsed = parseSort(sort);
        if (!parsed) return filtered;
        const dir = parsed.dir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name) * dir);
    }, [load, search, sort]);

    const searching = search.trim() !== "";
    const showTable = load.status !== "error" && !(load.status === "ready" && searching && rows.length === 0);
    useEffect(() => {
        const wrapper = tableRef.current?.parentElement;
        if (!wrapper) return;
        const ro = new ResizeObserver(([entry]) => entry && setTableWidth(entry.contentRect.width));
        ro.observe(wrapper);
        setTableWidth(wrapper.clientWidth);
        return () => ro.disconnect();
    }, [showTable]);
    const ACTIONS_WIDTH = 56;
    const flexWidth = Math.max(560, tableWidth) - ACTIONS_WIDTH;
    const colWidth = { name: flexWidth * 0.42, by: flexWidth * 0.3, date: flexWidth * 0.28, actions: ACTIONS_WIDTH };
    const colStyle = (w: number) => ({ width: w, minWidth: w });

    if (load.status === "error") return <FilesError onRetry={() => setReloadKey((k) => k + 1)} />;

    // The drop zone and the table card are `shrink-0`: as overflow-hidden
    // flex children of this scrolling column they would otherwise shrink to
    // fit and clip the rows instead of letting the column scroll.

    return (
        <div className="atlas-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-6 md:px-5" data-autotest="policy-files-folder-view">
            <Heading title={folder.name} folderName={folder.name} onBack={onBack} />
            <DropZone />

            {load.status === "ready" && searching && rows.length === 0 ? (
                <ListEmptyState description="Try a different search" />
            ) : (
                <div className="border-secondary mt-4 shrink-0 overflow-hidden rounded-xl border">
                    <Table
                        ref={tableRef}
                        aria-label={`${folder.name} files`}
                        data-autotest="policy-files-table"
                        className="table-fixed"
                        wrapperClassName="atlas-scrollbar overflow-x-auto"
                    >
                        <Table.Header className="bg-secondary">
                            <Table.Head id="name" isRowHeader style={colStyle(colWidth.name)}>
                                <SortHeader field="name" sort={sort} onSortChange={setSort}>
                                    File Name
                                </SortHeader>
                            </Table.Head>
                            <Table.Head id="by" style={colStyle(colWidth.by)}>
                                <PlainHeader>Uploaded by</PlainHeader>
                            </Table.Head>
                            <Table.Head id="date" style={colStyle(colWidth.date)}>
                                <PlainHeader>Upload date</PlainHeader>
                            </Table.Head>
                            <Table.Head id="actions" style={colStyle(colWidth.actions)}>
                                <span className="sr-only">Actions</span>
                            </Table.Head>
                        </Table.Header>
                        <Table.Body items={load.status === "loading" ? Array.from({ length: 5 }, (_, i) => ({ id: `skeleton-${i}` }) as unknown as PolicyFile) : rows}>
                            {(file) =>
                                load.status === "loading" ? (
                                    <Table.Row id={file.id}>
                                        <Table.Cell>
                                            <Skeleton className="h-4 w-48" />
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Skeleton className="h-6 w-36" />
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Skeleton className="h-4 w-24" />
                                        </Table.Cell>
                                        <Table.Cell />
                                    </Table.Row>
                                ) : (
                                    <Table.Row id={file.id} data-autotest="policy-files-row">
                                        <Table.Cell>
                                            <span className="text-secondary block truncate text-sm" title={file.name}>
                                                {file.name}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="flex min-w-0 items-center gap-2">
                                                {file.uploadedBy && <Avatar size="xs" initials={initialsOf(file.uploadedBy)} alt={file.uploadedBy} />}
                                                <span className="text-primary truncate text-sm font-medium">{file.uploadedBy ?? "-"}</span>
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-secondary text-sm tabular-nums">{formatDate(file.uploadedAt)}</span>
                                        </Table.Cell>
                                        <Table.Cell className="text-right">
                                            <RowActionsMenu
                                                label={`Actions for ${file.name}`}
                                                data-autotest="policy-files-row-menu"
                                                onAction={(key) => {
                                                    if (key === "download") downloadFile(file);
                                                }}
                                            >
                                                <AriaMenuItem id="preview" isDisabled className={MENU_ITEM_DISABLED}>
                                                    <Eye aria-hidden="true" className="text-fg-quaternary size-4" />
                                                    <span className="text-secondary text-sm font-medium">Preview</span>
                                                </AriaMenuItem>
                                                <AriaMenuItem id="download" isDisabled={!file.downloadUrl} className={MENU_ITEM}>
                                                    <Download01 aria-hidden="true" className="text-fg-quaternary size-4" />
                                                    <span className="text-secondary text-sm font-medium">Download</span>
                                                </AriaMenuItem>
                                                <AriaSeparator className={ROW_ACTIONS_SEPARATOR_CLASS} />
                                                <AriaMenuItem id="delete" isDisabled className={MENU_ITEM_DISABLED}>
                                                    <Trash01 aria-hidden="true" className="text-fg-error-secondary size-4" />
                                                    <span className="text-error-primary text-sm font-medium">Delete</span>
                                                </AriaMenuItem>
                                            </RowActionsMenu>
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            }
                        </Table.Body>
                    </Table>
                </div>
            )}

        </div>
    );
}

/** Single-line menu rows (icon + label), on the shared menu's item style. */
const MENU_ITEM = cx(ROW_ACTIONS_ITEM_CLASS, "flex-row! items-center gap-2!");
/** Actions the backend does not support yet (preview, delete). */
const MENU_ITEM_DISABLED = cx(MENU_ITEM, "cursor-not-allowed opacity-50");

/** The design's upload area, disabled: the backend has no upload for a
 *  policy term's files yet. */
function DropZone() {
    return (
        <div
            aria-disabled="true"
            data-autotest="policy-files-dropzone"
            className="bg-primary border-secondary flex shrink-0 cursor-not-allowed flex-col items-center gap-3 rounded-xl border px-6 py-4 text-center opacity-60"
        >
            <FeaturedIcon icon={UploadCloud02} color="gray" theme="modern" size="md" />
            <div className="flex flex-col gap-1">
                <p className="text-tertiary text-sm">
                    <button type="button" disabled className="text-brand-secondary cursor-not-allowed font-semibold" data-autotest="policy-files-button-upload">
                        Click to upload
                    </button>{" "}
                    or drag and drop
                </p>
                <p className="text-tertiary text-xs">PDF, DOCX, XLSX, PNG or JPG (max. 800×400px)</p>
            </div>
        </div>
    );
}
